/**
 * ============================================
 * Plan Context Provider
 * ============================================
 * 
 * Provides plan information and access control throughout the app
 */
import React, { createContext, useContext, useState, useEffect } from 'react';
import planService from '../services/planService';

const PlanContext = createContext();

export const usePlan = () => {
  const context = useContext(PlanContext);
  if (!context) {
    throw new Error('usePlan must be used within a PlanProvider');
  }
  return context;
};

export const PlanProvider = ({ children }) => {
  const [planInfo, setPlanInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch plan info on mount and when business_id changes
  useEffect(() => {
    fetchPlanInfo();
    
    // Listen for storage changes (when user logs in)
    const handleStorageChange = (e) => {
      if (e.key === 'business_id' || e.key === 'user_id') {
        console.log('[PLAN CONTEXT] User logged in, refreshing plan info');
        fetchPlanInfo();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Also listen for custom event (for same-tab login)
    const handleLoginEvent = () => {
      console.log('[PLAN CONTEXT] Login event detected, refreshing plan info');
      setTimeout(() => fetchPlanInfo(), 500); // Small delay to ensure localStorage is set
    };
    
    window.addEventListener('userLoggedIn', handleLoginEvent);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userLoggedIn', handleLoginEvent);
    };
  }, []);

  const fetchPlanInfo = async () => {
    try {
      setLoading(true);
      const businessUserId = localStorage.getItem('business_id');
      
      if (!businessUserId) {
        console.warn('[PLAN CONTEXT] No business_id found');
        setPlanInfo({
          plan_id: 'basic',
          plan_name: 'Basic',
          isReadOnly: true,
          limits: planService.getPlanLimits('basic'),
          hasActivePlan: false
        });
        setLoading(false);
        return;
      }

      const info = await planService.getPlanInfo(businessUserId);
      setPlanInfo(info);
      setError(null);
    } catch (err) {
      console.error('[PLAN CONTEXT] Error fetching plan:', err);
      setError(err.message);
      // Set to basic plan on error
      setPlanInfo({
        plan_id: 'basic',
        plan_name: 'Basic',
        isReadOnly: true,
        limits: planService.getPlanLimits('basic'),
        hasActivePlan: false
      });
    } finally {
      setLoading(false);
    }
  };

  // Refresh plan info (call after plan purchase/upgrade)
  const refreshPlan = async () => {
    await fetchPlanInfo();
  };

  // Check if a feature is available
  const hasFeature = (feature) => {
    if (!planInfo) return false;
    return planService.hasFeature(planInfo, feature);
  };

  // Check if can add product
  const canAddProduct = (currentCount) => {
    if (!planInfo) return { allowed: false, reason: 'Plan info not loaded' };
    return planService.canAddProduct(planInfo, currentCount);
  };

  // Check if can add domain
  const canAddDomain = (currentCount) => {
    if (!planInfo) return { allowed: false, reason: 'Plan info not loaded' };
    return planService.canAddDomain(planInfo, currentCount);
  };

  // Get upgrade message
  const getUpgradeMessage = (feature) => {
    return planService.getUpgradeMessage(feature);
  };

  const value = {
    planInfo,
    loading,
    error,
    refreshPlan,
    hasFeature,
    canAddProduct,
    canAddDomain,
    getUpgradeMessage,
    isReadOnly: planInfo?.isReadOnly || false,
    hasActivePlan: planInfo?.hasActivePlan || false
  };

  return (
    <PlanContext.Provider value={value}>
      {children}
    </PlanContext.Provider>
  );
};

export default PlanContext;
